// =============================================================================
// CLOSED BANNER TEXT (spec-defined strings)
// =============================================================================
const CLOSED_BANNER = {
    USER_CLOSED: {
        title: "Conversación cerrada",
        body: "Esta conversación fue cerrada. Puedes crear una nueva si lo necesitas.",
    },
    INACTIVITY_TIMEOUT: {
        title: "Cerrada automáticamente",
        body: "Esta conversación se cerró por inactividad. Puedes crear una nueva si lo necesitas.",
    },
    REFERENCE_INVALIDATED: {
        title: "Referencia no válida",
        body: "Esta conversación fue cerrada porque la reserva o servicio relacionado ya no existe.",
    },
    DEFAULT: {
        title: "Conversación cerrada",
        body: "Esta conversación ya no está activa.",
    },
};
// =============================================================================
// SERVICE
// =============================================================================
export const conversationService = {
    isActive(conversation) {
        return conversation.status === "ACTIVE";
    },
    /**
     * Finds an ACTIVE conversation that matches the given lookup parameters.
     * Used for duplicate detection before creating a new conversation.
     */
    findDuplicate(conversations, params) {
        return (conversations.find((c) => {
            if (c.status !== "ACTIVE")
                return false;
            if (c.service_id !== params.serviceId)
                return false;
            if (params.itineraryId && c.itinerary_id !== params.itineraryId)
                return false;
            if (params.bookingId && c.booking_id !== params.bookingId)
                return false;
            if (params.slotId && c.slot_id !== params.slotId)
                return false;
            return true;
        }) ?? null);
    },
    /** Sorts by last_message_at DESC (null values go to the end). Does not mutate the input. */
    sortByLastMessage(conversations) {
        return [...conversations].sort((a, b) => {
            if (!a.last_message_at && !b.last_message_at)
                return 0;
            if (!a.last_message_at)
                return 1;
            if (!b.last_message_at)
                return -1;
            return (new Date(b.last_message_at).getTime() -
                new Date(a.last_message_at).getTime());
        });
    },
    /** Client-side filtering by itinerary, service, and date range (on created_at). */
    filterConversations(conversations, filters) {
        return conversations.filter((c) => {
            if (filters.itineraryId && c.itinerary_id !== filters.itineraryId)
                return false;
            if (filters.serviceId && c.service_id !== filters.serviceId)
                return false;
            if (filters.dateFrom) {
                const from = new Date(filters.dateFrom);
                const created = new Date(c.created_at);
                if (created < from)
                    return false;
            }
            if (filters.dateTo) {
                const to = new Date(filters.dateTo);
                to.setHours(23, 59, 59, 999);
                const created = new Date(c.created_at);
                if (created > to)
                    return false;
            }
            return true;
        });
    },
    /** Returns the banner title/body text for a closed conversation. */
    getClosedBannerText(reason) {
        return CLOSED_BANNER[reason] ?? CLOSED_BANNER.DEFAULT;
    },
    /** Returns up to 2 uppercase initials from a name (first letter of first + last word). */
    getInitials(name) {
        const parts = name.trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0)
            return "?";
        if (parts.length === 1)
            return parts[0][0].toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },
    /** Deterministic avatar color from id. Returns #9CA3AF when closed=true. */
    getAvatarColor(id, closed = false) {
        if (closed)
            return "#9CA3AF";
        const palette = [
            "#E8472A",
            "#6366F1",
            "#8B5CF6",
            "#EC4899",
            "#0EA5E9",
            "#10B981",
        ];
        const hash = Array.from(id).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
        return palette[hash % palette.length];
    },
};
export default conversationService;
